import { useState, useCallback, useEffect, useRef } from 'react'
import { apiGet } from '../../../../../generic/Api_Functions'
import useMosInfo from '../../../common/provider/useMosInfo'
import { useSmartInterval } from '../../../../../generic/hooks/useSmartInterval'

/**
 * Hook to manage job data fetching and selection logic
 * @returns {Object} { jobProgress, activeItems, isLoading, error, fabricationOrder, jobReference, node, reloadProgress }
 */
export const useJobData = () => {
  const { fabricationOrder, jobReference, node, setGlobalData, activeJobs, refreshActiveJobs } = useMosInfo()
  const [jobProgress, setJobProgress] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [materials, setMaterials] = useState([])
  const lastMaterialsWorkflowIdRef = useRef(null)
  const lastMaterialsFoIdRef = useRef(null)
  const materialsSignatureRef = useRef('')
  const foId = fabricationOrder?.id
  const jrId = jobReference?.id

  const setSharedJobProgress = useCallback((progress) => {
    setJobProgress(progress)

    setGlobalData((prev) => {
      const nextProgress = progress && foId && jrId
        ? {
            fabricationOrderId: foId,
            jobReferenceId: jrId,
            available: progress.available,
            planned: progress.planned,
            done: progress.done,
            fail: progress.fail,
            total: progress.total,
            plannedEffective: progress.plannedEffective,
            reported: progress.reported,
            remaining: progress.remaining,
            percentage: progress.percentage
          }
        : null

      const previousProgress = prev?.currentJobProgress
      if (
        previousProgress?.fabricationOrderId === nextProgress?.fabricationOrderId &&
        previousProgress?.jobReferenceId === nextProgress?.jobReferenceId &&
        previousProgress?.reported === nextProgress?.reported &&
        previousProgress?.remaining === nextProgress?.remaining &&
        previousProgress?.plannedEffective === nextProgress?.plannedEffective &&
        previousProgress?.done === nextProgress?.done &&
        previousProgress?.fail === nextProgress?.fail
      ) {
        return prev
      }

      return { ...prev, currentJobProgress: nextProgress }
    })
  }, [foId, jrId, setGlobalData])

  // Load progress info for the selected job
  const loadInfo = useCallback(async () => {
    if (!fabricationOrder?.id || !jobReference?.id) {
      return null
    }
    try {
      setIsLoading(true)
      const response = await apiGet(`mos/fabricationOrder/${fabricationOrder.id}/progress?includeRework=true`)
      const job = response?.jobs?.find(job => job.id === jobReference.id)
      if (!job) return null
      const available = job.available ?? 0
      const done = job.done ?? 0
      const fail = job.fail ?? 0
      const planned = job.quantity ?? 0
      const reported = done + fail
      const plannedEffective = planned > 0 ? planned : (reported + available)
      const remaining = Math.max(plannedEffective - reported, 0)
      const percentage = plannedEffective > 0 ? (reported / plannedEffective) * 100 : 0
      const canFinish = job.canFinish ?? false
      const isFirst = job.first ?? false
      return {
        available,
        planned,
        done,
        fail,
        total: response.total ?? 0,
        plannedEffective,
        reported,
        remaining,
        percentage,
        canFinish,
        isFirst
      }
    } catch (err) {
      console.error(err)
      setError(err)
      return null
    } finally {
      setIsLoading(false)
    }
  }, [fabricationOrder?.id, jobReference?.id])

  useSmartInterval(loadInfo, 5000, setSharedJobProgress)

  useEffect(() => {
    setSharedJobProgress(null)
    loadInfo().then(setSharedJobProgress)
  }, [fabricationOrder?.id, jobReference?.id, loadInfo, setSharedJobProgress])

  // Load materials based on the first active job's workflow
  useEffect(() => {
    let cancelled = false

    const loadMaterials = async () => {
      if (!Array.isArray(activeJobs) || activeJobs.length === 0) {
        lastMaterialsFoIdRef.current = null
        lastMaterialsWorkflowIdRef.current = null
        materialsSignatureRef.current = ''
        setMaterials([])
        return
      }

      const first = activeJobs[0]
      let workflowId = first?.fabricationOrder?.workflow?.id ?? null

      if (!workflowId && first?.fabricationOrder?.id && lastMaterialsFoIdRef.current !== first.fabricationOrder.id) {
        lastMaterialsFoIdRef.current = first.fabricationOrder.id
        try {
          const response = await apiGet(`workflowIdByFabricationOrder/${first.fabricationOrder.id}`)
          if (cancelled) return
          workflowId = response?.result ?? null
        } catch (resourceError) {
          console.error('Error fetching workflow for resources', resourceError)
        }
      }

      if (workflowId && lastMaterialsWorkflowIdRef.current !== workflowId) {
        lastMaterialsWorkflowIdRef.current = workflowId
        try {
          const resources = await apiGet(`getResourcesByWorkflowId/${workflowId}`)
          if (cancelled) return
          const nodes = resources?.nodes || []
          const sig = Array.isArray(nodes) ? `${nodes.length}:${nodes.map(n => n?.id ?? '').join('|')}` : ''
          if (sig !== materialsSignatureRef.current) {
            materialsSignatureRef.current = sig
            setMaterials(nodes)
          }
        } catch (resourceError) {
          console.error('Error fetching resources', resourceError)
          if (!cancelled) setMaterials([])
        }
      } else if (!workflowId) {
        lastMaterialsWorkflowIdRef.current = null
        if (!cancelled) setMaterials([])
      }
    }

    loadMaterials()
    return () => { cancelled = true }
  }, [activeJobs])

  const reloadProgress = useCallback(() => loadInfo().then(setSharedJobProgress), [loadInfo, setSharedJobProgress])

  return {
    jobProgress,
    activeItems: activeJobs,
    isLoading,
    error,
    fabricationOrder,
    jobReference,
    node,
    reloadProgress,
    refreshActiveJobs,
    materials
  }
}
